'use client';

import { observer } from 'mobx-react-lite';
import type { Order } from '@instamenu/context-checkout';
import { formatCurrency } from '@instamenu/formatters';
import * as styles from './order-summary.css';

interface OrderSummaryProps {
  order: Order;
  /** Optional cap on rendered line items; the rest are summarized as "+N more". */
  maxItems?: number;
}

/**
 * Read-only mini summary used on the Table order-complete screen. Lists each
 * line item with quantity and price. Falls back gracefully when the order has
 * no items attached (defensive — the post-checkout order should always have
 * them, but we don't want a hard render error if upstream data is missing).
 */
export const OrderSummary = observer(({ order, maxItems = 5 }: OrderSummaryProps) => {
  const items = order.orderItems ?? [];
  if (items.length === 0) return null;

  const visible = items.slice(0, maxItems);
  const hiddenCount = items.length - visible.length;
  const orderNumberLabel = order.id ? `Order #${String(order.id).slice(-4).toUpperCase()}` : null;

  return (
    <div className={styles.wrapper} data-id="order-complete-order-summary">
      {orderNumberLabel ? <div className={styles.orderNumber}>{orderNumberLabel}</div> : null}
      {visible.map((item) => (
        <div key={item.id ?? item.key} className={styles.itemRow}>
          <span className={styles.itemName}>
            {item.quantity}× {item.item.name}
          </span>
          {/* `item.price` is already the line total (`unitPrice * quantity`),
              see `libs/context-checkout/src/lib/order-item.ts`. */}
          <span className={styles.itemPrice}>{formatCurrency(item.price)}</span>
        </div>
      ))}
      {hiddenCount > 0 ? <div className={styles.orderNumber}>+ {hiddenCount} more</div> : null}
    </div>
  );
});
